import React, { useState, useEffect, useRef } from 'react';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import './CameraCapture.css';

/**
 * Reusable camera capture component that supports both face detection (MediaPipe) 
 * and document capture modes.
 * 
 * @param {Object} props
 * @param {'face'|'document'} props.mode - Capture mode: 'face' for face detection, 'document' for document capture
 * @param {'user'|'environment'} props.facingMode - Camera facing mode (default: 'user' for face, 'environment' for document)
 * @param {Function} props.onCapture - Callback when image is captured, receives (blob, metadata)
 * @param {boolean} props.showCameraSelector - Show camera selector dropdown (default: true for face, false for document)
 * @param {string} props.instruction - Instruction text to display (for face mode)
 * @param {Function} props.onInstructionChange - Callback when instruction changes (for face mode)
 * @param {boolean} props.autoCapture - Auto-capture when conditions are met (default: true for face, false for document)
 * @param {number} props.countdown - Countdown seconds for manual capture (for document mode)
 * @param {Function} props.onCountdownChange - Callback when countdown changes (for document mode)
 * @param {boolean} props.disabled - Disable camera controls
 * @param {React.Ref} props.videoRef - Optional ref to video element
 */
function CameraCapture({
  mode = 'face',
  facingMode = mode === 'face' ? 'user' : 'environment',
  onCapture,
  showCameraSelector = mode === 'face',
  instruction,
  onInstructionChange,
  autoCapture = mode === 'face',
  countdown,
  onCountdownChange,
  disabled = false,
  videoRef: externalVideoRef
}) {
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const faceMeshRef = useRef(null);
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);
  const isCapturingRef = useRef(false);
  const isClosedRef = useRef(false);
  
  // Face detection state
  const captureTimeoutRef = useRef(null);
  const captureStartedRef = useRef(false);
  const faceDetectedRef = useRef(false);
  const goodFramesRef = useRef(0);
  const latestHandsRef = useRef(null);
  const livenessRef = useRef({
    startedAt: 0,
    leftDone: false,
    rightDone: false,
    centered: false
  });
  
  // Camera state
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Document capture countdown state
  const countdownIntervalRef = useRef(null);

  // Helper functions for face detection
  const bboxFromLandmarks = (landmarks) => {
    let minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (const p of landmarks) {
      if (typeof p?.x !== 'number' || typeof p?.y !== 'number') continue;
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  };

  const isBboxWellPositioned = (bbox) => {
    const edgeMargin = 0.04;
    return (
      bbox.minX > edgeMargin &&
      bbox.minY > edgeMargin &&
      bbox.maxX < 1 - edgeMargin &&
      bbox.maxY < 1 - edgeMargin
    );
  };

  const isHandOverFace = (faceBbox, handsResults) => {
    const hands = handsResults?.multiHandLandmarks;
    if (!Array.isArray(hands) || hands.length === 0) return false;

    const pad = 0.04;
    const minX = Math.max(0, faceBbox.minX - pad);
    const minY = Math.max(0, faceBbox.minY - pad);
    const maxX = Math.min(1, faceBbox.maxX + pad);
    const maxY = Math.min(1, faceBbox.maxY + pad);

    for (const hand of hands) {
      if (!Array.isArray(hand)) continue;
      for (const p of hand) {
        if (typeof p?.x !== 'number' || typeof p?.y !== 'number') continue;
        if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) {
          return true;
        }
      }
    }
    return false;
  };

  const getYawDirection = (landmarks) => {
    const nose = landmarks?.[1];
    const leftCheek = landmarks?.[234];
    const rightCheek = landmarks?.[454];
    if (!nose || !leftCheek || !rightCheek) return null;
    const denom = rightCheek.x - leftCheek.x;
    if (Math.abs(denom) < 1e-6) return null;
    const ratio = (nose.x - leftCheek.x) / denom;
    if (ratio < 0.42) return 'right';
    if (ratio > 0.58) return 'left';
    return 'center';
  };

  const resetCaptureState = () => {
    goodFramesRef.current = 0;
    faceDetectedRef.current = false;
    captureStartedRef.current = false;
    livenessRef.current.startedAt = 0;
    livenessRef.current.leftDone = false;
    livenessRef.current.rightDone = false;
    livenessRef.current.centered = false;
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
      captureTimeoutRef.current = null;
    }
  };

  const updateInstruction = (text) => {
    if (onInstructionChange && text !== instruction) {
      onInstructionChange(text);
    }
  };

  const startCameraWithDevice = async (deviceId) => {
    // Stop existing camera
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (e) {
        console.error('Error stopping camera:', e);
      }
      cameraRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    try {
      const ua = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(ua);

      const tryGetStream = async (constraints) => {
        return await navigator.mediaDevices.getUserMedia(constraints);
      };

      let stream;
      try {
        const primaryVideoConstraints = isIOS
          ? {
              facingMode: facingMode,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          : {
              deviceId: deviceId ? { exact: deviceId } : undefined,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            };

        stream = await tryGetStream({ video: primaryVideoConstraints });
      } catch (e1) {
        const name = e1?.name || '';
        console.warn('Primary getUserMedia constraints failed, retrying with fallback constraints:', e1);

        if (name === 'NotReadableError' || name === 'OverconstrainedError') {
          stream = await tryGetStream({
            video: {
              facingMode: facingMode,
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
          });
        } else {
          throw e1;
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;

        try {
          await videoRef.current.play();
        } catch (e) {
          // Some browsers block play() until enough data is buffered; ignore.
        }

        await new Promise((resolve) => {
          if (!videoRef.current) return resolve();
          if (videoRef.current.readyState >= 1) return resolve();
          const onMeta = () => {
            videoRef.current?.removeEventListener('loadedmetadata', onMeta);
            resolve();
          };
          videoRef.current.addEventListener('loadedmetadata', onMeta);
        });

        if (mode === 'face') {
          // Use MediaPipe Camera for face detection
          const camera = new Camera(videoRef.current, {
            onFrame: async () => {
              if (!videoRef.current || !isMountedRef.current || isCapturingRef.current) return;
              
              try {
                if (handsRef.current && !isCapturingRef.current) {
                  await handsRef.current.send({ image: videoRef.current });
                }
                if (faceMeshRef.current && !isCapturingRef.current) {
                  await faceMeshRef.current.send({ image: videoRef.current });
                }
              } catch (e) {
                if (!isCapturingRef.current) {
                  console.error('Error processing frame:', e);
                }
              }
            },
            width: 1280,
            height: 720
          });
          camera.start();
          cameraRef.current = camera;
        }
        
        setCameraActive(true);
        setError(null);
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setError('Unable to access camera. Please check your permissions.');
      setCameraActive(false);
    }
  };

  const handleCameraChange = async (deviceId) => {
    setSelectedCameraId(deviceId);
    if (deviceId) {
      await startCameraWithDevice(deviceId);
    }
  };

  const captureImage = async () => {
    if (isCapturingRef.current || !videoRef.current) return;
    
    isCapturingRef.current = true;
    
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || video.offsetWidth;
      canvas.height = video.videoHeight || video.offsetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });

      if (!blob) {
        throw new Error('Failed to create image blob');
      }

      // Stop camera and MediaPipe
      if (cameraRef.current) {
        try {
          cameraRef.current.stop();
        } catch (e) {
          console.error('Error stopping camera:', e);
        }
        cameraRef.current = null;
      }
      
      if (mode === 'face') {
        if (handsRef.current) {
          try {
            handsRef.current.close();
          } catch (e) {
            console.error('Error closing hands:', e);
          }
          handsRef.current = null;
        }
        
        if (faceMeshRef.current) {
          try {
            faceMeshRef.current.close();
          } catch (e) {
            console.error('Error closing face mesh:', e);
          }
          faceMeshRef.current = null;
        }
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (onCapture) {
        onCapture(blob, {
          width: canvas.width,
          height: canvas.height
        });
      }
    } catch (error) {
      console.error('Error capturing image:', error);
      isCapturingRef.current = false;
      setError('Failed to capture image. Please try again.');
    }
  };

  const handleManualCapture = () => {
    if (!cameraActive || !videoRef.current || disabled) return;
    
    if (countdown !== undefined && onCountdownChange) {
      // Start countdown
      let count = countdown;
      onCountdownChange(count);
      
      countdownIntervalRef.current = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          onCountdownChange(null);
          captureImage();
        } else {
          onCountdownChange(count);
        }
      }, 1000);
    } else {
      captureImage();
    }
  };

  // Watch for countdown changes from parent (for external trigger)
  useEffect(() => {
    if (mode === 'document' && countdown !== null && countdown !== undefined && countdown > 0 && onCountdownChange && cameraActive && !countdownIntervalRef.current) {
      // Parent triggered countdown, start it
      let currentCount = countdown;
      countdownIntervalRef.current = setInterval(() => {
        currentCount--;
        if (currentCount <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          onCountdownChange(null);
          captureImage();
        } else {
          onCountdownChange(currentCount);
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, mode, cameraActive]);

  // Initialize MediaPipe for face mode
  useEffect(() => {
    if (mode !== 'face') return;

    isMountedRef.current = true;
    isClosedRef.current = false;

    const initializeFaceMesh = async () => {
      try {
        const hands = new Hands({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
          }
        });

        hands.setOptions({
          maxNumHands: 2,
          modelComplexity: 1,
          minDetectionConfidence: 0.6,
          minTrackingConfidence: 0.6
        });

        hands.onResults((results) => {
          latestHandsRef.current = results;
        });

        handsRef.current = hands;

        const faceMesh = new FaceMesh({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
          }
        });

        faceMesh.setOptions({
          maxNumFaces: 2,
          refineLandmarks: true,
          minDetectionConfidence: 0.6,
          minTrackingConfidence: 0.6
        });

        faceMesh.onResults((results) => {
          if (!isMountedRef.current || isCapturingRef.current || !autoCapture) return;

          const faces = results?.multiFaceLandmarks || [];
          const faceCount = faces.length;

          if (faceCount === 0) {
            resetCaptureState();
            updateInstruction('Position your face');
            return;
          }

          if (faceCount > 1) {
            resetCaptureState();
            updateInstruction('Only one person should be in view');
            return;
          }

          const landmarks = faces[0];
          const bbox = bboxFromLandmarks(landmarks);

          const minFaceSize = 0.18;
          if (bbox.width < minFaceSize || bbox.height < minFaceSize) {
            resetCaptureState();
            updateInstruction('Move closer');
            return;
          }

          if (!isBboxWellPositioned(bbox)) {
            resetCaptureState();
            updateInstruction('Center your face');
            return;
          }

          if (isHandOverFace(bbox, latestHandsRef.current)) {
            resetCaptureState();
            updateInstruction('Remove your hand from your face');
            return;
          }

          // Liveness gate: look left + look right, then center face
          const now = Date.now();
          
          if (!livenessRef.current.startedAt) {
            livenessRef.current.startedAt = now;
          }

          const yawDir = getYawDirection(landmarks);
          if (yawDir === 'left') livenessRef.current.leftDone = true;
          if (yawDir === 'right') livenessRef.current.rightDone = true;

          if (!livenessRef.current.leftDone || !livenessRef.current.rightDone) {
            goodFramesRef.current = 0;
            faceDetectedRef.current = false;
            captureStartedRef.current = false;
            livenessRef.current.centered = false;
            if (captureTimeoutRef.current) {
              clearTimeout(captureTimeoutRef.current);
              captureTimeoutRef.current = null;
            }
            if (!livenessRef.current.leftDone) {
              updateInstruction('Now look left');
            } else {
              updateInstruction('Now look right');
            }
            return;
          }

          if (yawDir === 'center') {
            livenessRef.current.centered = true;
          } else {
            livenessRef.current.centered = false;
            goodFramesRef.current = 0;
            faceDetectedRef.current = false;
            captureStartedRef.current = false;
            if (captureTimeoutRef.current) {
              clearTimeout(captureTimeoutRef.current);
              captureTimeoutRef.current = null;
            }
            updateInstruction('Look straight at the camera');
            return;
          }

          goodFramesRef.current += 1;
          const requiredGoodFrames = 10;
          if (goodFramesRef.current < requiredGoodFrames) {
            faceDetectedRef.current = false;
            captureStartedRef.current = false;
            updateInstruction('Hold still...');
            return;
          }

          if (!faceDetectedRef.current) {
            faceDetectedRef.current = true;
            updateInstruction('Hold still...');
          }

          if (!captureStartedRef.current) {
            captureStartedRef.current = true;
            captureTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current && !isCapturingRef.current) {
                captureImage();
              }
            }, 1000);
          }
        });

        faceMeshRef.current = faceMesh;

        // Request camera permission first to get device labels
        try {
          const permissionStream = await navigator.mediaDevices.getUserMedia({ video: true });
          permissionStream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          console.error('Error requesting camera permission:', e);
        }

        // Get available cameras
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        
        // Set default camera
        let defaultCameraId = videoDevices.length > 0 ? videoDevices[0].deviceId : null;
        if (facingMode === 'user') {
          const userFacing = videoDevices.find(d => d.label && (d.label.toLowerCase().includes('front') || d.label.toLowerCase().includes('user')));
          if (userFacing) {
            defaultCameraId = userFacing.deviceId;
          }
        }
        setSelectedCameraId(defaultCameraId);

        // Start camera with selected device
        if (videoRef.current && defaultCameraId) {
          await startCameraWithDevice(defaultCameraId);
        }
      } catch (error) {
        console.error('Error initializing face mesh:', error);
        setError('Failed to initialize camera. Please try again.');
      }
    };

    initializeFaceMesh();

    return () => {
      const liveness = livenessRef.current;
      isMountedRef.current = false;
      captureStartedRef.current = false;
      faceDetectedRef.current = false;
      isCapturingRef.current = false;
      goodFramesRef.current = 0;
      liveness.startedAt = 0;
      liveness.leftDone = false;
      liveness.rightDone = false;
      liveness.centered = false;
      
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }
      
      if (cameraRef.current) {
        try {
          cameraRef.current.stop();
        } catch (e) {
          console.error('Error stopping camera in cleanup:', e);
        }
        cameraRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      if (faceMeshRef.current && !isClosedRef.current) {
        try {
          isClosedRef.current = true;
          faceMeshRef.current.close();
        } catch (e) {
          console.error('Error closing face mesh:', e);
        }
        faceMeshRef.current = null;
      }
      if (handsRef.current) {
        try {
          handsRef.current.close();
        } catch (e) {
          console.error('Error closing hands:', e);
        }
        handsRef.current = null;
      }
    };
  }, [mode, facingMode, autoCapture]);

  // Initialize simple camera for document mode
  useEffect(() => {
    if (mode !== 'document') return;

    const startCamera = async () => {
      try {
        setError(null);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        
        // Wait for video element to be available (it should be after render)
        let retries = 0;
        const maxRetries = 20; // Max 1 second of retries
        const waitForVideo = () => {
          if (videoRef.current) {
            console.log('Setting video stream, video element:', videoRef.current);
            videoRef.current.srcObject = stream;
            streamRef.current = stream;
            
            // Ensure video plays
            videoRef.current.play()
              .then(() => {
                console.log('Video playing successfully');
                setCameraActive(true);
              })
              .catch(e => {
                // Some browsers block play() until enough data is buffered; ignore.
                console.warn('Video play error (may be expected):', e);
                setCameraActive(true); // Still mark as active even if play() fails
              });
          } else if (retries < maxRetries) {
            retries++;
            // Retry after a short delay if video element isn't ready
            setTimeout(waitForVideo, 50);
          } else {
            console.error('Video element not found after retries');
            setError('Video element not available');
          }
        };
        
        waitForVideo();
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Unable to access camera. Please check your permissions.');
        setCameraActive(false);
      }
    };

    // Start camera after component has rendered
    const timer = setTimeout(startCamera, 0);

    return () => {
      clearTimeout(timer);
      const video = videoRef.current;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
      setCameraActive(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };

    return () => {
      const video = videoRef.current;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
      setCameraActive(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, facingMode]);

  return (
    <div className="camera-capture">
      {showCameraSelector && cameras.length > 1 && (
        <div className="camera-selector">
          <label htmlFor="camera-select">Camera:</label>
          <select
            id="camera-select"
            value={selectedCameraId || ''}
            onChange={(e) => handleCameraChange(e.target.value)}
            disabled={disabled}
          >
            {cameras.map((camera) => (
              <option key={camera.deviceId} value={camera.deviceId}>
                {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {error ? (
        <div className="camera-error">
          <p>{error}</p>
        </div>
      ) : (
        <div className="camera-frame">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
            style={{ transform: mode === 'face' ? 'scaleX(-1)' : 'none' }}
          />
          {countdown !== null && countdown !== undefined && (
            <div className="countdown-overlay">
              <div className="countdown-number">{countdown}</div>
            </div>
          )}
        </div>
      )}

      {mode === 'document' && !error && !onCountdownChange && (
        <div className="camera-actions">
          <button
            className="camera-capture-button"
            onClick={handleManualCapture}
            disabled={!cameraActive || disabled || (countdown !== null && countdown !== undefined)}
          >
            Take photo
          </button>
        </div>
      )}
    </div>
  );
}

export default CameraCapture;
